import React, { useMemo, useState } from "react";
import { Heatmap, Unavailable } from "./FootballPitch";
import { aggregateHeatmaps, heatmapThirds } from "./fiData";
import "./fi-extra.css";

const pct = (share) => `${Math.round(share * 100)}%`;

/**
 * Action-density heatmap for one match or summed across every sampled match
 * that carries a grid. Spatial grids exist for only a few cached matches, so
 * the component always states how many matches it is built from.
 */
export default function HeatmapExplorer({ heatmaps = [], matchName = () => "", sampleSize, hideCoverage = false }) {
  const [choice, setChoice] = useState("all");
  const merged = useMemo(() => aggregateHeatmaps(heatmaps), [heatmaps]);

  if (!heatmaps.length) {
    return (
      <Unavailable
        text={`No action-density grids exist for the ${sampleSize ?? "selected"} sampled matches. Spatial grids are cached for only a small number of matches — try "Full cached season" in the sample filter.`}
      />
    );
  }

  const single = heatmaps.find((h) => String(h.fixture_id) === choice);
  const current = choice === "all" && merged && heatmaps.length > 1 ? merged : single || (merged && heatmaps.length === 1 ? { ...merged, fixture_id: heatmaps[0].fixture_id } : null);
  const thirds = heatmapThirds(current);

  return (
    <div>
      <div className="fx-toolbar">
        {heatmaps.length > 1 && (
          <label className="fx-field">Source
            <select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Heatmap source">
              <option value="all">All {heatmaps.length} matches (summed)</option>
              {heatmaps.map((h) => (
                <option key={h.fixture_id} value={h.fixture_id}>{matchName(h.fixture_id) || h.fixture_id}</option>
              ))}
            </select>
          </label>
        )}
        {!hideCoverage && <span className="fx-coverage">
          Spatial grid available for <b>{heatmaps.length}</b> of <b>{sampleSize ?? "?"}</b> sampled matches
          {current && !(current.matches > 1) && current.fixture_id && matchName(current.fixture_id) ? <> · showing <b>{matchName(current.fixture_id)}</b></> : null}
        </span>}
      </div>

      <Heatmap data={current} />

      {thirds && (
        <div style={{ marginTop: 14 }}>
          <div className="fx-meter" role="img" aria-label={`Share of observed actions by third: defensive ${pct(thirds.defensive)}, middle ${pct(thirds.middle)}, attacking ${pct(thirds.attacking)}`}>
            <i style={{ width: pct(thirds.defensive) }} /><i style={{ width: pct(thirds.middle) }} /><i style={{ width: pct(thirds.attacking) }} />
          </div>
          <div className="fx-meter-legend">
            <span>Defensive third <b>{pct(thirds.defensive)}</b></span>
            <span>Middle third <b>{pct(thirds.middle)}</b></span>
            <span>Attacking third <b>{pct(thirds.attacking)}</b></span>
          </div>
          <p className="fi-note">Thirds are counted from the provider's grid columns; they describe where actions were observed, not possession or territory.</p>
        </div>
      )}
      {merged?.skipped > 0 && choice === "all" && (
        <p className="fi-note">{merged.skipped} grid(s) with a different resolution or orientation were not combined.</p>
      )}
    </div>
  );
}
